import { useEffect, useRef, useState } from "react";
import { ZoomControls } from "./ZoomControls";
import { HIGHLIGHT_BG, HIGHLIGHT_OUTLINE } from "./highlightStyle";

// The strong hover tint is the SHARED one (STK-1360/STK-1811): the mail-body
// quote highlighter paints the same colour, so it has a single definition.
// Re-exported here because the text preview reads it from this module.
export { HIGHLIGHT_BG, HIGHLIGHT_OUTLINE } from "./highlightStyle";

// STK-2338 reverse hover: a fainter, neutral "resting" tint painted while the
// user hovers the SOURCE document row's flow in the recap (as opposed to the
// strong amber tint above, which marks a flow-hovered row). Distinctly fainter
// than HIGHLIGHT_BG so strong-vs-resting stays legible at a glance.
// Alpha matches `RESTING_FILL` (regionGeometry.ts, "#6B72801F" = 31/255 ≈
// 0.12) so PDF/photo and html/text resting reads at the same weight.
export const RESTING_HTML_BG = "rgba(107, 114, 128, 0.12)";
export const RESTING_HTML_OUTLINE = "1px solid rgba(107, 114, 128, 0.28)";

// Addressable html elements, mirroring the producer's rule in
// `document_analysis_html.number_html_rows`: `<tr>` when the doc has any table
// rows, else block-level leaves. Producer `find_all(sel)` and viewer
// `querySelectorAll(sel)` enumerate the identical raw html in document order, so
// the 1-based ordinal is an exact address (STK-1360).
const HTML_ROW_SELECTOR = "tr";
const HTML_BLOCK_SELECTOR = "p,li,h1,h2,h3,h4,h5,h6,blockquote";

/**
 * Which selector addresses `doc`: `<tr>` when the doc has any table row, else
 * the block-level leaf selector. Exported so the hover listener resolves
 * `closest()` against the identical selector `htmlAddressableElements` used to
 * build its list — STK-2338's ordinal contract requires ONE selector choice,
 * not two call sites that happen to agree.
 */
export function addressableSelector(doc: Document): string {
  return doc.querySelector(HTML_ROW_SELECTOR)
    ? HTML_ROW_SELECTOR
    : HTML_BLOCK_SELECTOR;
}

/**
 * The addressable elements, in document order. The 1-based index into THIS
 * list is the ordinal both the producer (`number_html_rows`) and the hover
 * listener speak (STK-1360 / STK-2338). `applyHtmlRowHighlight` and the
 * delegated hover listener both call this — never a bespoke
 * `querySelectorAll` — so the two can never drift onto different lists.
 */
export function htmlAddressableElements(doc: Document): HTMLElement[] {
  return [...doc.querySelectorAll<HTMLElement>(addressableSelector(doc))];
}

/**
 * Resolve the 1-based ordinal of the addressable element under a hover
 * event's `target`, or `null` when the pointer sits over nothing addressable.
 * Extracted out of the hover listener as a pure function (STK-2338 fix
 * round 1) so the round trip against `applyHtmlRowHighlight` — the actual
 * proof that the two paths can't drift onto different element lists — is a
 * test, not a comment: `hoveredOrdinal` finds the element via `closest` +
 * `htmlAddressableElements`, and `applyHtmlRowHighlight` re-derives the same
 * list independently; feeding one's output ordinal into the other must land
 * back on the exact element that was hovered.
 */
export function hoveredOrdinal(
  doc: Document,
  target: EventTarget | null,
): number | null {
  const el = (target as HTMLElement | null)?.closest<HTMLElement>(
    addressableSelector(doc),
  );
  if (!el) return null;
  const index = htmlAddressableElements(doc).indexOf(el);
  return index >= 0 ? index + 1 : null;
}

/** Per-element paint bookkeeping: the author's OWN inline background/outline,
 *  plus which marker (if any) this element currently carries.
 *
 *  Held in a `WeakMap` keyed on the element rather than in `data-*` attributes
 *  on it. The document is CUSTOMER html: writing `data-sl-*` onto it clobbers
 *  any attribute of the same name the author already used, and reading our
 *  state back out of an attribute the author also writes would restore the
 *  wrong background on cleanup (cubic P2). A WeakMap cannot collide with
 *  document content at all, and entries die with the element — which matters
 *  here because every srcDoc reload replaces the whole element set. */
interface PaintState {
  origBackground: string;
  origOutline: string;
  marker: "strong" | "resting";
}
const paintState = new WeakMap<HTMLElement, PaintState>();

/** Restore the author's own inline styling and forget the element. */
function clearPaint(el: HTMLElement, state: PaintState): void {
  el.style.background = state.origBackground;
  el.style.outline = state.origOutline;
  paintState.delete(el);
}

/**
 * Paint the hovered flow's `rows` onto a rendered html document by element
 * ordinal, and `restingRows` (STK-2338: the row whose flow is hovered from the
 * OTHER direction, doc → flow) with a fainter neutral tint. `rows` and
 * `restingRows` are 1-based indices into `htmlAddressableElements(doc)` — the
 * same set the producer numbered. A row in both sets renders STRONG only: the
 * user's live hover always wins over the resting marker. Clears every
 * addressable element that carries either marker but lands in neither set, so
 * an empty call (mouse-out) or a changed hover resets cleanly and no element
 * ever carries both markers at once. Pure DOM, so it is unit-tested against a
 * DOMParser document without depending on iframe/srcDoc behavior.
 */
export function applyHtmlRowHighlight(
  doc: Document,
  rows: number[],
  restingRows: number[] = [],
): void {
  const strong = new Set(rows);
  const resting = new Set(restingRows);
  htmlAddressableElements(doc).forEach((el, idx) => {
    const ordinal = idx + 1;
    const existing = paintState.get(el);
    const marker = markerFor(ordinal, strong, resting);

    if (!marker) {
      if (existing) clearPaint(el, existing);
      return;
    }
    // Snapshot the author's OWN inline background/outline before the FIRST
    // paint, so cleanup restores them instead of assigning "" — real customer
    // html (e.g. an inline `<tr style="background:...">` striping rule) must
    // survive a highlight-then-clear cycle. Never re-snapshot: the second read
    // would capture our own tint.
    const state: PaintState = existing
      ? { ...existing, marker }
      : {
          origBackground: el.style.background,
          origOutline: el.style.outline,
          marker,
        };
    paintState.set(el, state);
    if (marker === "strong") {
      el.style.background = HIGHLIGHT_BG;
      el.style.outline = HIGHLIGHT_OUTLINE;
    } else {
      el.style.background = RESTING_HTML_BG;
      el.style.outline = RESTING_HTML_OUTLINE;
    }
  });
}

/** Which marker an ordinal earns — strong wins when it is in both sets. */
function markerFor(
  ordinal: number,
  strong: Set<number>,
  resting: Set<number>,
): PaintState["marker"] | null {
  if (strong.has(ordinal)) return "strong";
  return resting.has(ordinal) ? "resting" : null;
}

/**
 * HTML preview that renders the RAW html blob in a sandboxed iframe (unchanged
 * from before) and, on hover, highlights the flow's source rows by element
 * ordinal via `applyHtmlRowHighlight`. `sandbox="allow-same-origin"` keeps the
 * parent's reach into `contentDocument`, and the highlight re-applies whenever
 * the html reloads or the hovered rows change (STK-1360).
 */
const HTML_MIN_SCALE = 0.25;
const HTML_MAX_SCALE = 5;
const HTML_SCALE_STEP = 0.25;

const clampHtmlScale = (s: number) =>
  Math.min(HTML_MAX_SCALE, Math.max(HTML_MIN_SCALE, s));

export function HtmlPreviewWithHighlight({
  htmlContent,
  attachmentName,
  highlightedRows,
  restingRows,
  onRowHover,
  scale,
  onScaleChange,
  onClose,
  onDownload,
  isDownloading,
}: Readonly<{
  htmlContent: string;
  attachmentName: string;
  highlightedRows: number[];
  /** STK-2338: rows to tint faintly because their flow is hovered from the
   * recap side (doc → flow is the STRONG direction; this is the resting one). */
  restingRows?: number[];
  /** STK-2338: fired as the pointer moves over an addressable element, with its
   * 1-based ordinal, or `null` once it leaves every addressable element. */
  onRowHover?: (ordinal: number | null) => void;
  scale: number;
  onScaleChange: (s: number) => void;
  onClose: () => void;
  onDownload: () => void;
  isDownloading: boolean;
}>) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Bumped on each srcDoc load so the highlight effect re-runs against the
  // freshly-parsed contentDocument (a new htmlContent reloads the iframe).
  const [loadTick, setLoadTick] = useState(0);
  // Kept current so the (once-attached) wheel listener always zooms off the
  // latest scale without re-binding on every scale change.
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const onScaleChangeRef = useRef(onScaleChange);
  onScaleChangeRef.current = onScaleChange;

  useEffect(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    applyHtmlRowHighlight(doc, highlightedRows, restingRows);
    // `loadTick` re-runs this effect after each srcDoc reload, so `htmlContent`
    // is not needed here — adding it would fire against the stale document
    // before onLoad, which is harmless but wasteful.
  }, [highlightedRows, restingRows, loadTick]);

  // STK-2338 reverse hover: delegated on contentDocument (not per-element) so
  // it survives the srcDoc reload cycle with the rest of this iframe's
  // listeners, rebinding on each `loadTick`. Resolves via `hoveredOrdinal`,
  // the same resolver the round-trip test exercises against
  // `applyHtmlRowHighlight` — see that function's docstring.
  useEffect(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc || !onRowHover) return;
    const onOver = (ev: Event) => onRowHover(hoveredOrdinal(doc, ev.target));
    const onLeave = () => onRowHover(null);
    doc.addEventListener("mouseover", onOver);
    doc.addEventListener("mouseleave", onLeave);
    return () => {
      doc.removeEventListener("mouseover", onOver);
      doc.removeEventListener("mouseleave", onLeave);
    };
  }, [loadTick, onRowHover]);

  // Ctrl/⌘ + wheel zoom, mirroring ExcelViewer. The wheel event fires inside
  // the sandboxed iframe (allow-same-origin keeps its document reachable), so
  // the listener lives on the iframe's own document — a listener on the parent
  // never sees wheels that land over the iframe.
  useEffect(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const onWheel = (ev: WheelEvent) => {
      if (!ev.ctrlKey && !ev.metaKey) return;
      ev.preventDefault();
      const step = ev.deltaY < 0 ? HTML_SCALE_STEP : -HTML_SCALE_STEP;
      onScaleChangeRef.current(clampHtmlScale(scaleRef.current + step));
    };
    doc.addEventListener("wheel", onWheel, { passive: false });
    return () => doc.removeEventListener("wheel", onWheel);
  }, [loadTick]);

  return (
    <div className="flex flex-col h-full">
      <ZoomControls
        scale={scale}
        onScaleChange={(s) => onScaleChange(clampHtmlScale(s))}
        onReset={() => onScaleChange(1)}
        onClose={onClose}
        onDownload={onDownload}
        isDownloading={isDownloading}
      />
      <div className="flex-1 overflow-auto relative">
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            // Counteract only when zooming IN so the scaled iframe fills the
            // scroll container (its content then reflows + magnifies like
            // browser zoom). When zooming OUT we keep 100% so the layout box
            // never grows past the container and spawns spurious scrollbars.
            width: `${100 / Math.max(scale, 1)}%`,
            height: `${100 / Math.max(scale, 1)}%`,
          }}
        >
          <iframe
            ref={iframeRef}
            srcDoc={htmlContent}
            sandbox="allow-same-origin"
            onLoad={() => setLoadTick((t) => t + 1)}
            className="w-full h-full border-0 bg-white"
            title={attachmentName}
          />
        </div>
      </div>
    </div>
  );
}
